/**
 * DateRangePicker Component
 * Date range picker for expense filtering and reporting
 */

import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  maxDate?: Date;
  minDate?: Date;
}

const presetRanges = [
  {
    label: 'Today',
    getValue: () => {
      const today = new Date();
      return { startDate: today, endDate: today };
    },
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return { startDate: yesterday, endDate: yesterday };
    },
  },
  {
    label: 'Last 7 days',
    getValue: () => {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 6);
      return { startDate, endDate };
    },
  },
  {
    label: 'Last 30 days',
    getValue: () => {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 29);
      return { startDate, endDate };
    },
  },
  {
    label: 'This month',
    getValue: () => {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate, endDate };
    },
  },
  {
    label: 'Last month',
    getValue: () => {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate, endDate };
    },
  },
  {
    label: 'This quarter',
    getValue: () => {
      const now = new Date();
      const quarter = Math.floor(now.getMonth() / 3);
      const startDate = new Date(now.getFullYear(), quarter * 3, 1);
      const endDate = new Date(now.getFullYear(), quarter * 3 + 3, 0);
      return { startDate, endDate };
    },
  },
  {
    label: 'This year',
    getValue: () => {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), 0, 1);
      const endDate = new Date(now.getFullYear(), 11, 31);
      return { startDate, endDate };
    },
  },
];

export function DateRangePicker({
  value = { startDate: null, endDate: null },
  onChange,
  placeholder = 'Select date range',
  label,
  error,
  disabled = false,
  className,
  maxDate,
  minDate,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateRange = () => {
    if (!value.startDate && !value.endDate) return placeholder;
    if (value.startDate && !value.endDate) return `${formatDate(value.startDate)} - ...`;
    if (!value.startDate && value.endDate) return `... - ${formatDate(value.endDate)}`;
    if (value.startDate && value.endDate) {
      if (value.startDate.getTime() === value.endDate.getTime()) {
        return formatDate(value.startDate);
      }
      return `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`;
    }
    return placeholder;
  };

  const handleDateClick = (date: Date) => {
    if (selectingStart) {
      onChange?.({ startDate: date, endDate: null });
      setSelectingStart(false);
    } else {
      const startDate = value.startDate;
      if (startDate && date < startDate) {
        onChange?.({ startDate: date, endDate: startDate });
      } else {
        onChange?.({ startDate, endDate: date });
      }
      setSelectingStart(true);
      setIsOpen(false);
    }
  };

  const handlePresetClick = (preset: typeof presetRanges[0]) => {
    const range = preset.getValue();
    onChange?.(range);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange?.({ startDate: null, endDate: null });
    setSelectingStart(true);
  };

  const isDateInRange = (date: Date) => {
    if (!value.startDate || !value.endDate) return false;
    return date >= value.startDate && date <= value.endDate;
  };

  const isDateSelected = (date: Date) => {
    return (
      (value.startDate && date.getTime() === value.startDate.getTime()) ||
      (value.endDate && date.getTime() === value.endDate.getTime())
    );
  };

  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    // const lastDay = new Date(year, month + 1, 0); // not used
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentMonth(newMonth);
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={cn('relative w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div
        ref={dropdownRef}
        className={cn(
          'relative border rounded-md bg-white',
          error ? 'border-error-500' : 'border-gray-300',
          disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer',
          'focus-within:ring-2 focus-within:ring-primary-600 focus-within:border-primary-600'
        )}
      >
        <div
          className="flex items-center justify-between p-3"
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className={cn(
              'text-sm',
              value.startDate || value.endDate ? 'text-gray-900' : 'text-gray-400'
            )}>
              {formatDateRange()}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {(value.startDate || value.endDate) && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <svg
              className={cn(
                'w-4 h-4 text-gray-400 transition-transform',
                isOpen && 'transform rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="flex">
              {/* Presets */}
              <div className="w-48 p-2 border-r border-gray-200">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Quick Select
                </h4>
                <div className="space-y-1">
                  {presetRanges.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => handlePresetClick(preset)}
                      className="w-full text-left px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calendar */}
              <div className="w-80 p-4">
                {/* Calendar header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={() => navigateMonth('prev')}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h3 className="text-sm font-medium text-gray-900">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h3>
                  <button
                    type="button"
                    onClick={() => navigateMonth('next')}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Day names */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map((day) => (
                    <div key={day} className="text-xs font-medium text-gray-500 text-center py-1">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((date, index) => {
                    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isSelected = isDateSelected(date);
                    const isInRange = isDateInRange(date);
                    const isDisabled = isDateDisabled(date);

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => !isDisabled && handleDateClick(date)}
                        disabled={isDisabled}
                        className={cn(
                          'w-8 h-8 text-xs rounded transition-colors',
                          !isCurrentMonth && 'text-gray-300',
                          isCurrentMonth && !isSelected && !isInRange && 'text-gray-700 hover:bg-gray-100',
                          isToday && !isSelected && 'bg-primary-100 text-primary-700',
                          isSelected && 'bg-primary-600 text-white',
                          isInRange && !isSelected && 'bg-primary-100 text-primary-700',
                          isDisabled && 'cursor-not-allowed opacity-50'
                        )}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>

                {/* Action buttons */}
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    {selectingStart ? 'Select start date' : 'Select end date'}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClear}
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsOpen(false)}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-error-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}
